import React, { useEffect } from 'react';
import { usePortletStore } from './App';

import { useViewStateStore } from './stores/viewState.store';
import { BrowserCheck } from './components/BrowserSupport/BrowserCheck';
import { Disclaimer } from './components/Info/Disclaimer';
import { SelectRegionAndInstitution } from './components/RegionAndInstitution/SelectRegionAndInstitution';
import { EnterEmail } from './components/Email/EnterEmail';
import { WaitingRoom } from './components/Waiting/WaitingRoom';
import { VideoConference } from './components/VideoConf/VideoConference';
import { FinalScreen } from './components/ThankYouScreen/FinalScreen';
import { ConnectAppointment } from './components/Appointment/ConnectAppointment';

import { LoadMask } from 'ehs-react';
import { Registration } from './components/Registration/Registration';
import { PublicHeader } from './components/Header/PublicHeader';
import { useWaitingRoomStore } from './stores/waitingRoom.store';
import { setAutoFreeze } from 'immer';
import { AppointmentSelectionMenu } from './components/Appointment/AppointmentSelectionMenu';
import { useCallStore } from './stores/call.store';
import {
  useCallMenuStore,
  DeviceSelection,
  DeviceOverview
} from 'virtual-visit-common';

setAutoFreeze(false);

export function MainView(): JSX.Element /*NOSONAR: code is as easy as it can get*/ {
  const [
    viewStateData,
    loadInitialState,
    loading,
    browserCheckState,
    setDeviceSelected,
    nextState
  ] = useViewStateStore(state => [
    state.viewStateData,
    state.load,
    state.loading,
    state.viewStateData?.browserCheckState,
    state.setDeviceSelected,
    state.nextState
  ]);

  const [deviceSettingsMandatory] = usePortletStore(state => [
    state.configuration.deviceSettingsMandatory
  ]);

  const [waitingRoomTab] = useWaitingRoomStore(state => [state.waitingRoomTab]);

  const [callInfoStateCallback] = useCallStore(store => [
    store.callInfoStateCallback
  ]);

  useEffect(() => {
    loadInitialState({
      userAgent: navigator.userAgent
    });
  }, [loadInitialState]);

  useEffect(() => {
    if (viewStateData) {
      if (!viewStateData.loggedInPatient) {
        const nav = document.getElementById('navigation');
        if (nav) {
          nav.remove();
        }
        const banner = document.getElementById('banner');
        if (banner) {
          banner.remove();
        }
      }
    }
  }, [viewStateData]);

  console.log('VIEW STATE DATA', viewStateData);

  let mainContainerCls = 'vvpp-main';

  const [
    preSelectCamera,
    preSelectMicrophone,
    preSelectSpeaker
  ] = useCallMenuStore(store => [
    store.preSelectCamera,
    store.preSelectMicrophone,
    store.preSelectSpeaker
  ]);

  let currentPage = <div>{viewStateData?.state}</div>;
  if (viewStateData?.state === 'BROWSER_CHECK') {
    currentPage = <BrowserCheck />;
  } else if (viewStateData?.state === 'DISCLAIMER') {
    currentPage = <Disclaimer />;
  } else if (viewStateData?.state === 'APPOINTMENT_SELECTION_MENU') {
    currentPage = <AppointmentSelectionMenu />;
  } else if (viewStateData?.state === 'DEVICE_OVERVIEW') {
    currentPage = <DeviceOverview nextState={nextState} />;
  } else if (viewStateData?.state === 'DEVICE_SELECTION') {
    currentPage = (
      <DeviceSelection
        setDeviceSelected={setDeviceSelected}
        nextState={nextState}
        browserCheckState={browserCheckState}
        isDeviceSettingsMandatory={deviceSettingsMandatory}
        callInfoStateCallback={callInfoStateCallback}
        preSelectCamera={preSelectCamera}
        preSelectMicrophone={preSelectMicrophone}
        preSelectSpeaker={preSelectSpeaker}
      />
    );
    mainContainerCls += ' vvpp-main-device-selection';
  } else if (viewStateData?.state === 'REGION_AND_INSTITUTION') {
    currentPage = <SelectRegionAndInstitution />;
  } else if (viewStateData?.state === 'PERSONAL_FORM') {
    if (viewStateData.loggedInPatient) {
      currentPage = <EnterEmail />;
    } else {
      currentPage = <Registration />;
      mainContainerCls += ' vvpp-main-registration';
    }
  } else if (viewStateData?.state === 'WAITING_ROOM') {
    currentPage = <WaitingRoom />;
    if (waitingRoomTab === 'CHAT') {
      mainContainerCls += ' vvpp-main-waiting-room-chat';
    }
  } else if (viewStateData?.state === 'VIDEO_CONFERENCE') {
    currentPage = <VideoConference />;
    mainContainerCls = `${mainContainerCls} vvpp-main-video-conf`;
  } else if (viewStateData?.state === 'FINAL_SCREEN') {
    currentPage = <FinalScreen />;
  } else if (viewStateData?.state === 'CONNECT_APPOINTMENT') {
    currentPage = <ConnectAppointment />;
    mainContainerCls += ' vvpp-main-registration';
  }

  const patPortalCls = viewStateData?.loggedInPatient
    ? ' vvpp-patient-portal'
    : '';

  return (
    <div className={'virtual-visit-patient-portlet' + patPortalCls}>
      <VideoConferenceProviderScript />
      <LoadMask masked={loading} />
      {!viewStateData?.loggedInPatient &&
        viewStateData?.state !== 'VIDEO_CONFERENCE' && <PublicHeader />}
      <div className={mainContainerCls}>{currentPage}</div>
    </div>
  );
}

function VideoConferenceProviderScript(): JSX.Element {
  const [videoProvider, jitsiServer] = usePortletStore(state => [
    state.configuration.videoProvider,
    state.configuration.jitsiServer
  ]);

  useEffect(() => {
    if (videoProvider === 'JITSI') {
      const script = document.createElement('script');
      script.src = jitsiServer.replace(/\/$/, '') + '/external_api.js';
      script.async = true;
      script.onload = () => {
        console.log('jitsi script loaded');
      };
      document.body.appendChild(script);
    }
  }, [videoProvider, jitsiServer]);

  return <React.Fragment />;
}
